import React from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { incrementForever, take } from "../../../util/iterators";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ResetZoomIcon from "./ResetZoomIcon";

/**
 * Much like how `window.open` allows any JS code on the page to trigger the
 * opening of in a new window/tab with a specified URL, this module provides a
 * mechanism to allow any JS code to trigger the opening of a dialog that
 * displays PDFs by providing a URL that points to a PDF.
 */

/*
 * This snippet is a necessary step in initialising the PDF preview
 * functionality. Taken from the example code for react-pdf.
 */
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

const PdfPreviewContext = React.createContext((_link: string) => {});

/**
 * Use the callable PDF preview component to display a PDF in a dialog.
 */
export function usePdfPreview(): {
  /**
   * Preview the PDF to be found at the passed URL. If the PDF cannot be
   * loaded then the dialog opens with the message "Failed to load PDF file".
   */
  openPdfPreview: (url: string) => void;
} {
  const openPdfPreview = React.useContext(PdfPreviewContext);
  return {
    openPdfPreview,
  };
}

/**
 * This components provides a mechanism for any other component that is its
 * descendent to trigger the previewing of a PDF by passing the PDF's URL to a
 * call to `usePdfPreview`'s `openPdfPreview`. Just do something like
 *    const { openPdfPreview } = usePdfPreview();
 *    openPdfPreview("http://example.com/document.pdf");
 */
export function CallablePdfPreview({ children }: { children: React.ReactNode }): React.ReactNode {
  const [pdfPreviewOpen, setPdfPreviewOpen] = React.useState<null | string>(null);
  const [numPages, setNumPages] = React.useState<number>(0);
  const [scale, setScale] = React.useState(1);
  const { t } = useTranslation(["gallery", "common"]);

  React.useEffect(() => {
    if (!pdfPreviewOpen) {
      setNumPages(0);
      setScale(1);
      return;
    }
    setNumPages(0);
    setScale(1);
  }, [pdfPreviewOpen]);

  function onDocumentLoadSuccess({ numPages: nextNumPages }: { numPages: number }): void {
    setNumPages(nextNumPages);
  }

  return (
    <>
      <PdfPreviewContext.Provider value={setPdfPreviewOpen}>{children}</PdfPreviewContext.Provider>
      {pdfPreviewOpen && (
        <Dialog
          open={true}
          fullWidth
          // Disabled to not clash with legacy jQuery dialogs. Can be removed once everything is Reactified
          disableEnforceFocus
          onClose={() => {
            setPdfPreviewOpen(null);
          }}
          sx={{
            // z-index to 2000 to ensure it still displays above legacy jQuery dialogs
            zIndex: 2000,
          }}
          aria-label={t("callablePdfPreview.title")}
        >
          <DialogContent sx={{ overflowY: "auto" }}>
            <Document file={pdfPreviewOpen} onLoadSuccess={onDocumentLoadSuccess}>
              {[...take(incrementForever(), numPages)].map((index) => (
                <Box key={`page_${index + 1}`}>
                  <Page pageNumber={index + 1} width={550} scale={scale} />
                </Box>
              ))}
            </Document>
          </DialogContent>
          <DialogActions>
            <ButtonGroup
              variant="outlined"
              sx={{
                border: "2px solid #cfc9d2",
                borderRadius: "8px",
              }}
            >
              <IconButton
                onClick={() => {
                  setScale(scale * 1.2);
                }}
                aria-label={t("common:actions.zoomIn")}
                size="small"
              >
                <ZoomInIcon />
              </IconButton>
              <Divider
                orientation="vertical"
                sx={{
                  height: "26px",
                  marginTop: "4px",
                  borderRightWidth: "1px",
                }}
              />
              <IconButton
                onClick={() => {
                  setScale(1);
                }}
                disabled={scale === 1}
                aria-label={t("common:actions.resetZoom")}
                size="small"
              >
                <ResetZoomIcon />
              </IconButton>
              <Divider
                orientation="vertical"
                sx={{
                  height: "26px",
                  marginTop: "4px",
                  borderRightWidth: "1px",
                }}
              />
              <IconButton
                onClick={() => {
                  setScale(scale / 1.2);
                }}
                aria-label={t("common:actions.zoomOut")}
                size="small"
              >
                <ZoomOutIcon />
              </IconButton>
            </ButtonGroup>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Button
              onClick={() => {
                setPdfPreviewOpen(null);
              }}
            >
              {t("common:actions.close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
